import { useReducer } from 'react';
import { useHistory } from 'react-router-dom';
import createReducer from 'helpers/reducers-helper';
import constate from 'constate';
import assign from 'lodash/assign';
import repo from 'service/repository';
import {
  useAppContext,
  SHOW_APP_MESSAGE,
  UPDATE_LICENSE_PLATE,
  UPDATE_USER_INFO,
  NOW_SETTING_LICENSE_PLATE,
} from 'use-app';
import { errorHandler } from 'helpers/error-handler';
import { PATH } from 'config';
import { isPlateNumber } from 'helpers/rule-validation';

export const INITIALIZE = 'initialize';
export const SELECT_LICENSE_PLATE = 'select_license_plate';
export const CHANGE_PLATE_NUMBER = 'change_plate_number';
export const CHANGE_PLATE_NUMBER_ERROR_MESSAGE = 'change_plate_number_error_message';
export const CHANGE_CAR_TYPE = 'change_car_type';
export const CLOSE_DIALOG = 'close_dialog';
export const ON_SUBMIT = 'on_submit';
export const HIDE_LOADING = 'hide_loading';
export const SET_SERVICE_DIALOG = 'set_service_dialog';
export const SET_ACTIVATED_LICENSE_PLATES = 'SET_ACTIVATED_LICENSE_PLATES';
export const SET_RENTAL_PLATE_DIALOG = 'SET_RENTAL_PLATE_DIALOG';
export const SET_CROSS_DISTRIBUTOR_BINDING_DIALOG = 'SET_CROSS_DISTRIBUTOR_BINDING_DIALOG';

const useLicensePlate = () => {
  const { state: appState, dispatch: appDispatch } = useAppContext();
  const history = useHistory();
  const initState = {
    id: '',
    plateNumber: '',
    plateNumberErrorMessage: '',
    vehicleType: 'car',
    loading: false,
    showDialog: false,
    bindingState: '',
    selectedLicensePlate: { id: '', plateNumber: '', vehicleType: '' },
    motoMochipayDialog: false,
    serviceDialog: false,
    activatedLicensePlates: [],
    showRentalPlateDialog: false,
    showCrossDistributorBindingPlateDialog: false,
  };

  const initialize = (state, payload) =>
    assign({}, state, {
      ...payload,
      loading: false,
      showDialog: false,
      showCrossDistributorBindingPlateDialog: false,
    });
  const changPlateNumber = (state, payload) => assign({}, state, { plateNumber: payload });
  const changePlateNumberErrorMessage = (state, payload) => assign({}, state, { plateNumberErrorMessage: payload });
  const changVehicleType = (state, payload) => assign({}, state, { vehicleType: payload });
  const onSubmit = state => assign({}, state, { loading: true });
  const selectLicensePlate = (state, payload) => {
    const bindingState = payload.cityParkinglots.taipeiCity.state;
    return assign({}, state, {
      showDialog: true,
      bindingState: bindingState,
      selectedLicensePlate: payload,
    });
  };
  const closeDialog = state => assign({}, state, { showDialog: false });
  const hideLoading = state => assign({}, state, { loading: false });
  const setServiceDialog = (state, payload) => assign({}, state, { serviceDialog: payload });
  const setActivatedLicensePlates = (state, payload) => assign({}, state, { activatedLicensePlates: payload });
  const setRentalPlateDialog = (state, payload) => assign({}, state, { showRentalPlateDialog: payload });
  const setCrossDistributorBindingPlateDialog = (state, payload) =>
    assign({}, state, { showCrossDistributorBindingPlateDialog: payload });

  const plateNumberFormCheck = plateNumber => {
    const isPlateNumberValid = isPlateNumber(plateNumber);
    const plateNumberErrorMessage = isPlateNumberValid ? '' : '車牌格式錯誤';

    dispatch({ type: CHANGE_PLATE_NUMBER_ERROR_MESSAGE, payload: plateNumberErrorMessage });

    return isPlateNumberValid;
  };

  const redirectAfterAddPlate = () => {
    if (appState.nowSettingLicensePlateAndCreditCard) {
      history.replace(PATH.MOCHIPAY_FLOW_SETTING);
    } else {
      history.goBack();
      dispatch({ type: INITIALIZE });
    }

    dispatch({
      type: SET_RENTAL_PLATE_DIALOG,
      payload: false,
    });

    appDispatch({
      type: SHOW_APP_MESSAGE,
      payload: {
        type: 'success',
        message: '車牌新增成功',
      },
    });
  };

  const redirectAfterEditPlate = () => {
    history.goBack();
    dispatch({ type: INITIALIZE });

    dispatch({
      type: SET_RENTAL_PLATE_DIALOG,
      payload: false,
    });

    appDispatch({
      type: SHOW_APP_MESSAGE,
      payload: {
        type: 'success',
        message: '車牌修改成功',
      },
    });
  };

  const addLicensePlate = ({ payload }) => {
    if (!plateNumberFormCheck(payload.plateNumber)) return;

    dispatch({ type: ON_SUBMIT });

    repo
      .addLicensePlate(payload.plateNumber, payload.vehicleType)
      .then(() => {
        // 如果已設定好付款、正在設定車牌
        if (appState.nowSettingLicensePlate) {
          if (appState.setFrom === 'autopay') {
            repo.updateUserInfo(undefined, undefined, undefined, true).then(data => {
              appDispatch({
                type: UPDATE_USER_INFO,
                payload: data,
              });
            });
          }
          appDispatch({
            type: NOW_SETTING_LICENSE_PLATE,
            payload: false,
          });
        }
      })
      .then(repo.fetchLicensePlates)
      .then(data => {
        appDispatch({
          type: UPDATE_LICENSE_PLATE,
          payload: data,
        });

        let newLicensePlate = data.find(d => d.plateNumber === payload.plateNumber);
        if (newLicensePlate.isRentalCar) {
          dispatch({
            type: SET_RENTAL_PLATE_DIALOG,
            payload: true,
          });
        } else {
          // 如果正在設定車牌、即將設定付款
          redirectAfterAddPlate();
        }
      })
      .catch(error => {
        appDispatch({
          type: SHOW_APP_MESSAGE,
          payload: {
            type: 'failure',
            message: errorHandler(error),
          },
        });
      })
      .finally(() => {
        dispatch({ type: HIDE_LOADING });
      });
  };

  const deleteLicensePlate = ({ payload }) => {
    repo
      .deleteLicensePlate(payload.id)
      .then(repo.fetchLicensePlates)
      .then(data => {
        appDispatch({
          type: UPDATE_LICENSE_PLATE,
          payload: data,
        });
        dispatch({ type: INITIALIZE });

        if (payload.vehicleIdentityCrossDistributorBinding) {
          dispatch({ type: SET_CROSS_DISTRIBUTOR_BINDING_DIALOG, payload: true });
        }
      })
      .catch(error => {
        appDispatch({
          type: SHOW_APP_MESSAGE,
          payload: {
            type: 'failure',
            message: errorHandler(error),
          },
        });
      })
      .finally(() => {
        dispatch({ type: HIDE_LOADING });
      });
  };

  const bindLicensePlate = id => {
    repo
      .bindLicensePlate(id)
      .then(repo.fetchLicensePlates)
      .then(data => {
        appDispatch({
          type: UPDATE_LICENSE_PLATE,
          payload: data,
        });
        dispatch({ type: INITIALIZE });
      })
      .catch(error => {
        appDispatch({
          type: SHOW_APP_MESSAGE,
          payload: {
            type: 'failure',
            message: errorHandler(error),
          },
        });
      })
      .finally(() => {
        dispatch({ type: HIDE_LOADING });
      });
  };

  const updateLicensePlate = ({ payload }) => {
    if (!plateNumberFormCheck(payload.plateNumber)) return;

    dispatch({ type: ON_SUBMIT });

    repo
      .modifyLicensePlate(payload.id, payload.plateNumber, payload.vehicleType)
      .then(repo.fetchLicensePlates)
      .then(data => {
        appDispatch({
          type: UPDATE_LICENSE_PLATE,
          payload: data,
        });

        let updatedLicensePlate = data.find(d => d.plateNumber === payload.plateNumber);

        if (updatedLicensePlate.isRentalCar) {
          dispatch({
            type: SET_RENTAL_PLATE_DIALOG,
            payload: true,
          });
        } else {
          redirectAfterEditPlate();
        }
      })
      .catch(error => {
        appDispatch({
          type: SHOW_APP_MESSAGE,
          payload: {
            type: 'failure',
            message: errorHandler(error),
          },
        });
      })
      .finally(() => {
        dispatch({ type: HIDE_LOADING });
      });
  };

  const reducer = createReducer(
    {
      [INITIALIZE]: initialize,
      [SELECT_LICENSE_PLATE]: selectLicensePlate,
      [CHANGE_PLATE_NUMBER]: changPlateNumber,
      [CHANGE_PLATE_NUMBER_ERROR_MESSAGE]: changePlateNumberErrorMessage,
      [CHANGE_CAR_TYPE]: changVehicleType,
      [CLOSE_DIALOG]: closeDialog,
      [ON_SUBMIT]: onSubmit,
      [HIDE_LOADING]: hideLoading,
      [SET_SERVICE_DIALOG]: setServiceDialog,
      [SET_ACTIVATED_LICENSE_PLATES]: setActivatedLicensePlates,
      [SET_RENTAL_PLATE_DIALOG]: setRentalPlateDialog,
      [SET_CROSS_DISTRIBUTOR_BINDING_DIALOG]: setCrossDistributorBindingPlateDialog,
    },
    initState,
    true
  );
  const [state, dispatch] = useReducer(reducer, initState);

  return {
    state,
    dispatch,
    addLicensePlate,
    updateLicensePlate,
    deleteLicensePlate,
    bindLicensePlate,
    redirectAfterAddPlate,
    redirectAfterEditPlate,
  };
};

const [LicensePlatesProvider, useLicensePlateContext] = constate(useLicensePlate);

export { LicensePlatesProvider, useLicensePlateContext };
